/** Format dispatch: bytes -> RawFrame. */
import type { RawFrame } from '../core/types';
import { detectFormat, type FormatId } from './formats';
import { decodePng } from './decoders/png';
import { decodeTiff } from './decoders/tiff';
import { decodePfm, decodePnm } from './decoders/netpbm';
import { decodeHdr } from './decoders/hdr';
import { decodeExr } from './decoders/exr';
import { decodeFits } from './decoders/fits';
import { decodeBrowser } from './decoders/browser';
import { DEFAULT_RAW, decodeRaw, type RawOptions } from './decoders/raw';

export interface DecodeOptions {
  raw?: RawOptions;
}

/** Decode image bytes of any supported format. */
export async function decodeBytes(bytes: Uint8Array, name: string, opt: DecodeOptions = {}): Promise<RawFrame> {
  const format: FormatId = detectFormat(bytes, name);
  switch (format) {
    case 'png':
      try {
        return decodePng(bytes, name);
      } catch (e) {
        // Exotic PNGs (e.g. APNG quirks): fall back to the browser decoder.
        if (typeof createImageBitmap === 'function') return decodeBrowser(bytes, name, 'png');
        throw e;
      }
    case 'tiff':
      return decodeTiff(bytes, name);
    case 'pfm':
      return decodePfm(bytes, name);
    case 'pnm':
      return decodePnm(bytes, name);
    case 'hdr':
      return decodeHdr(bytes, name);
    case 'exr':
      return decodeExr(bytes, name);
    case 'fits':
      return decodeFits(bytes, name);
    case 'raw':
      return decodeRaw(bytes, name, opt.raw ?? DEFAULT_RAW);
    case 'jpeg':
    case 'webp':
    case 'gif':
    case 'bmp':
    case 'avif':
      return decodeBrowser(bytes, name, format);
    default:
      throw new Error(`Unrecognised image format: ${name || 'clipboard data'}`);
  }
}
